import {
  ArrowLeftRight,
  CheckCheck,
  Pencil,
  Plus,
  QrCode,
  ScanLine,
  Search,
  Trash2,
  Undo2,
  Wrench,
} from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ASSET_OWNERSHIP_DESCRIPTIONS,
  ASSET_OWNERSHIP_LABELS,
  ASSET_OWNERSHIPS,
  ASSET_STATUSES,
  ASSET_STATUS_LABELS,
  canAssignAsset,
  canOpenRepair,
  canReturnAsset,
  type AssetOwnership,
  type AssetStatus,
} from '@cfl/assets-shared';

import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { DeviceArt, LostArt } from '@/components/illustrations';
import { Typewriter } from '@/components/typewriter';
import { CategoryPicker, SuggestionPicker } from '@/components/pickers';
import { PageContainer } from '@/components/shell/page-container';
import { AssetFormDialog } from '@/routes/asset-form';
import { AssignDialog } from '@/routes/assign';
import { ReturnDialog } from '@/routes/return';
import { OpenRepairDialog } from '@/routes/repairs';
import {
  BulkDeleteDialog,
  DeleteRecordDialog,
  SelectionBar,
  useSelection,
} from '@/components/bulk-delete';
import { useBulkDeleteAssets } from '@/lib/bulk-api';
import { Checkbox } from '@/components/cargofl-ui';
import {
  acknowledgementVariant,
  assetStatusVariant,
  ownershipVariant,
  PILL_CLASS,
  warrantyVariant,
  WARRANTY_LABELS,
} from '@/lib/badges';
import { SortableHead, type SortDirection } from '@/components/sortable-head';
import { ApiError } from '@/lib/api';
import { useAcknowledgeOnBehalf } from '@/lib/operations-api';
import {
  useAssetCategories,
  useAssets,
  useDeleteAsset,
  useLocationSuggestions,
  useVerifyAssets,
  type AssetSort,
  type AssetSummary,
} from '@/lib/assets-api';
import { useCan } from '@/lib/session';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 25;

/**
 * The register.
 *
 * Filters live in the URL, not in component state, so a filtered view can be sent to
 * a colleague and a browser Back actually goes back a filter rather than off the page.
 */
export function AssetsPage() {
  const [params, setParams] = useSearchParams();
  const canManage = useCan('manageAssets');
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const canDelete = useCan('deleteRecords');
  // Its own named predicate, not manageAssets — the API guards POST /assignments on
  // assignAssets, and this button must be gated on the same one it will be judged by.
  const canAssign = useCan('assignAssets');
  const canRepair = useCan('manageRepairs');
  // The row whose handover or return is being recorded. The register row carries
  // everything either dialog needs, accessories included, so nothing is fetched on open.
  const [issuing, setIssuing] = React.useState<AssetSummary | null>(null);
  const [returning, setReturning] = React.useState<AssetSummary | null>(null);
  const [repairing, setRepairing] = React.useState<AssetSummary | null>(null);
  // Ticking rows is not only for deleting them - IT confirms handovers in bulk from
  // the same selection, and IT cannot delete.
  const canSelect = canManage || canDelete;
  const [confirmingAck, setConfirmingAck] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const selection = useSelection();
  const bulkDelete = useBulkDeleteAssets();
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deletingOne, setDeletingOne] = React.useState<AssetSummary | null>(null);
  const deleteAsset = useDeleteAsset();

  const search = params.get('search') ?? '';
  const status = (params.get('status') ?? '') as AssetStatus | '';
  const ownership = (params.get('ownership') ?? '') as AssetOwnership | '';
  const categoryId = params.get('categoryId') ?? '';
  const warranty = (params.get('warranty') ?? '') as 'EXPIRED' | 'EXPIRING_SOON' | '';
  const verification = (params.get('verification') ?? '') as 'DUE' | '';
  const includeArchived = params.get('includeArchived') === 'true';
  const page = Number(params.get('page') ?? '1');
  const sort = (params.get('sort') ?? 'code') as AssetSort;
  const direction = (params.get('direction') ?? 'asc') as SortDirection;

  const { data: categories } = useAssetCategories();
  const { data, isPending } = useAssets({
    search,
    status,
    ownership,
    categoryId,
    warranty,
    verification,
    includeArchived,
    page,
    pageSize: PAGE_SIZE,
    sort,
    direction,
  });

  /** Any filter change resets to page 1: staying on page 4 of a now two-page result
   *  shows an empty table that looks like "no assets" rather than "no page 4". */
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '' || value === 'false') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const setSort = (column: AssetSort, next: SortDirection) => {
    const params2 = new URLSearchParams(params);
    params2.set('sort', column);
    params2.set('direction', next);
    params2.delete('page');
    setParams(params2, { replace: true });
  };

  // Debounced so a five-letter search is one request, not five.
  const [searchInput, setSearchInput] = React.useState(search);
  React.useEffect(() => setSearchInput(search), [search]);
  React.useEffect(() => {
    if (searchInput === search) return;
    const timer = setTimeout(() => setFilter('search', searchInput), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const total = data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <PageContainer>
      <Card>
        <CardContent className="space-y-4 py-4 sm:py-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              {!searchInput && (
                <span className="pointer-events-none absolute left-8 top-1/2 -translate-y-1/2 truncate text-sm text-muted-foreground">
                  <Typewriter text="Search by code, serial number, model or who has it…" />
                </span>
              )}
              <Input
                className="pl-8"
                aria-label="Search assets by code, serial number, model, or who has it"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>

            <Select
              value={status || 'all'}
              onValueChange={(v) => setFilter('status', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                {ASSET_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {ASSET_STATUS_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <CategoryPicker
              categories={categories}
              value={categoryId}
              onChange={(v) => setFilter('categoryId', v)}
              allLabel="Any type"
              showCounts
              className="w-[170px]"
            />

            <Select
              value={warranty || 'all'}
              onValueChange={(v) => setFilter('warranty', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Any warranty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any warranty</SelectItem>
                <SelectItem value="EXPIRING_SOON">Expiring soon</SelectItem>
                <SelectItem value="EXPIRED">Out of warranty</SelectItem>
              </SelectContent>
            </Select>

            {/* The stock-take worklist. One option, not a NEVER/OVERDUE pair: on the
                day this shipped every asset was NEVER, and the work is the same either
                way - go and look at it. */}
            <Select
              value={verification || 'all'}
              onValueChange={(v) => setFilter('verification', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Any check" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any check</SelectItem>
                <SelectItem value="DUE">Needs checking</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={ownership || 'all'}
              onValueChange={(v) => setFilter('ownership', v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[170px]">
                <SelectValue placeholder="Any ownership" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any ownership</SelectItem>
                {ASSET_OWNERSHIPS.map((o) => (
                  <SelectItem key={o} value={o}>
                    {ASSET_OWNERSHIP_LABELS[o]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button hint={"Also show retired and disposed assets"}
              variant={includeArchived ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setFilter('includeArchived', includeArchived ? 'false' : 'true')}
            >
              {includeArchived ? 'Hide archived' : 'Show archived'}
            </Button>

            {canManage && (
              <Button hint={"Add a new asset to the register"} size="sm" onClick={() => setFormOpen(true)}>
                <Plus className="mr-1.5 size-4" /> Register asset
              </Button>
            )}
          </div>

          {canSelect && (
            <SelectionBar
              count={selection.ids.length}
              noun="asset"
              onClear={selection.clear}
              onDelete={canDelete ? () => setConfirmingDelete(true) : undefined}
              actions={
                canManage ? (
                  <>
                    {/* The floor walk: tick what you can see in the room, stamp it all
                        at once. One at a time is thirty page loads for one room, which
                        is how a stock-take stops getting done. */}
                    <Button hint={"Record that you physically saw the selected assets today"} size="sm" onClick={() => setVerifying(true)}>
                      <ScanLine className="mr-1.5 size-4" /> Mark as seen
                    </Button>
                    <Button hint={"Print labels for the selected assets"} asChild size="sm" variant="secondary">
                      <Link to={`/assets/labels?ids=${selection.ids.join(',')}`}>
                        <QrCode className="mr-1.5 size-4" /> Print labels
                      </Link>
                    </Button>
                    <Button hint={"Record receipt for the selected assets on their holders' behalf"} size="sm" variant="secondary" onClick={() => setConfirmingAck(true)}>
                      <CheckCheck className="mr-1.5 size-4" /> Confirm receipt on their behalf
                    </Button>
                  </>
                ) : null
              }
            />
          )}

          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {canSelect && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        aria-label="Select every asset on this page"
                        checked={
                          (data?.assets.length ?? 0) > 0 &&
                          data!.assets.every((a) => selection.selected.has(a.id))
                        }
                        onCheckedChange={(on) =>
                          selection.toggleAll(data?.assets.map((a) => a.id) ?? [], on === true)
                        }
                      />
                    </TableHead>
                  )}
                  <SortableHead column="code" sort={sort} direction={direction} onSort={setSort} className="w-[110px]">
                    Code
                  </SortableHead>
                  <SortableHead column="name" sort={sort} direction={direction} onSort={setSort}>
                    Asset
                  </SortableHead>
                  <SortableHead column="category" sort={sort} direction={direction} onSort={setSort} className="hidden md:table-cell">
                    Type
                  </SortableHead>
                  <SortableHead column="serial" sort={sort} direction={direction} onSort={setSort} className="hidden lg:table-cell">
                    Serial
                  </SortableHead>
                  <SortableHead column="status" sort={sort} direction={direction} onSort={setSort}>
                    Status
                  </SortableHead>
                  <SortableHead column="ownership" sort={sort} direction={direction} onSort={setSort}>
                    Held by
                  </SortableHead>
                  {/* "Assigned to", not "Employee": the Held by pill beside it already
                      says Employee for a personal device, and the same word over the
                      next column read as if it labelled the same thing. */}
                  <SortableHead column="holder" sort={sort} direction={direction} onSort={setSort}>
                    Assigned to
                  </SortableHead>
                  <SortableHead column="acknowledged" sort={sort} direction={direction} onSort={setSort}>
                    Confirmed
                  </SortableHead>
                  <SortableHead column="warrantyEnd" sort={sort} direction={direction} onSort={setSort} className="hidden xl:table-cell">
                    Warranty
                  </SortableHead>
                  {/* Two labelled buttons fit: Assign or Return, plus Repair. */}
                  {canManage && <TableHead className={canDelete ? 'w-[250px]' : 'w-[210px]'} />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <TableRow>
                    <TableCell
                      colSpan={(canManage ? 10 : 9) + (canSelect ? 1 : 0)}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      Loading the register…
                    </TableCell>
                  </TableRow>
                ) : data?.assets.length ? (
                  data.assets.map((asset) => (
                    <TableRow
                      key={asset.id}
                      // The hatch says "out of service" without making the row hard to
                      // read - finance still reconciles against these.
                      className={cn(
                        (asset.status === 'RETIRED' || asset.status === 'DISPOSED') &&
                          'at-archived',
                      )}
                    >
                      {canSelect && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${asset.code}`}
                            checked={selection.selected.has(asset.id)}
                            onCheckedChange={() => selection.toggle(asset.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="at-num font-medium">
                        <Link to={`/assets/${asset.id}`} className="hover:underline">
                          {asset.code}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link to={`/assets/${asset.id}`} className="hover:underline">
                          <div className="font-medium">{asset.name}</div>
                          {(asset.brand ?? asset.model) && (
                            <div className="text-xs text-muted-foreground">
                              {[asset.brand, asset.model].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                        {asset.categoryName}
                      </TableCell>
                      <TableCell className="at-num hidden text-xs text-muted-foreground lg:table-cell">
                        {asset.serial ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={assetStatusVariant(asset.status)} className={PILL_CLASS}>
                          {ASSET_STATUS_LABELS[asset.status]}
                        </Badge>
                      </TableCell>
                      {/* Two questions, two columns: whose asset it is, and which person
                          has it. They were one cell, which put the name at a different x
                          in every row and left the pill reading like a prefix to it.
                          Drawn on EVERY row rather than only the employee-owned ones -
                          an asset nobody has been issued is held by the company, and a
                          dash there was the column refusing to answer itself. Company is
                          muted, employee is blue: the normal case must not be the loud
                          one. */}
                      <TableCell>
                        <Badge
                          variant={ownershipVariant(asset.ownership)}
                          className="shrink-0 rounded-full px-1.5 py-0 text-[10px]"
                          title={ASSET_OWNERSHIP_DESCRIPTIONS[asset.ownership]}
                        >
                          {asset.ownership === 'EMPLOYEE' ? 'Employee' : 'Company'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {asset.holder ? (
                          <Link
                            to={`/employees/${asset.holder.id}`}
                            className="whitespace-nowrap text-sm hover:underline"
                          >
                            {asset.holder.name}
                          </Link>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      {/* Its own column rather than a pill trailing the holder's name.
                          Pushed to the right of that cell it lined up but sat under no
                          heading, which reads as a column nobody labelled; left beside
                          the name it started at a different x in every row. A handover
                          nobody has confirmed earns a column anyway - it is what the
                          Monday chase is made of. */}
                      <TableCell>
                        {!asset.holder ? (
                          <span className="text-sm text-muted-foreground">—</span>
                        ) : asset.acknowledged ? (
                          // Deliberately not a green pill. Most handovers are confirmed,
                          // and a badge on every one of them is noise the eye has to step
                          // over to find the amber.
                          <span className="text-xs text-muted-foreground">confirmed</span>
                        ) : (
                          <Badge
                            variant={acknowledgementVariant(false)}
                            className="shrink-0 rounded-full px-1.5 py-0 text-[10px]"
                            title="Issued, but this person has not confirmed receiving it"
                          >
                            unconfirmed
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        <Badge variant={warrantyVariant(asset.warranty)} className={PILL_CLASS}>
                          {WARRANTY_LABELS[asset.warranty]}
                        </Badge>
                      </TableCell>
                      {canManage && (
                        <TableCell className="whitespace-nowrap text-right">
                          {/* Labelled, unlike the two icon buttons beside it, and only on
                              the rows it applies to. AVAILABLE is the minority state in a
                              healthy company, so an icon lost among icons on one row in
                              ten is a feature nobody finds.

                              The condition is the shared predicate the API judges the
                              request by, not a hand-written status check - a button on a
                              row the API will reject is worse than no button. Under
                              repair earns neither: the asset is at the vendor. */}
                          {canAssign && canAssignAsset(asset.status, asset.holder !== null) && (
                            <Button hint={"Hand this asset to an employee"}
                              size="sm"
                              variant="secondary"
                              className="mr-1 h-7 px-2 text-xs"
                              onClick={() => setIssuing(asset)}
                            >
                              <ArrowLeftRight className="mr-1 size-3.5" /> Assign
                            </Button>
                          )}
                          {canAssign && canReturnAsset(asset.holder !== null, asset.status) && (
                            <Button hint={"Record that the asset came back"}
                              size="sm"
                              variant="secondary"
                              className="mr-1 h-7 px-2 text-xs"
                              onClick={() => setReturning(asset)}
                            >
                              <Undo2 className="mr-1 size-3.5" /> Return
                            </Button>
                          )}
                          {/* Its own permission - IT owns repairs, and manageRepairs is
                              what the API guards the repair routes with. This one DOES
                              sit beside Assign or Return: anything available or out with
                              someone can also go to the vendor, which is what widened
                              the column to two labelled buttons. */}
                          {canRepair && canOpenRepair(asset.status) && (
                            <Button hint={"Record a repair or service"}
                              size="sm"
                              variant="secondary"
                              className="mr-1 h-7 px-2 text-xs"
                              onClick={() => setRepairing(asset)}
                            >
                              <Wrench className="mr-1 size-3.5" /> Repair
                            </Button>
                          )}
                          <Button hint={"Change this asset's details"}
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => setEditingId(asset.id)}
                          >
                            <Pencil className="size-3.5" />
                            <span className="sr-only">Edit {asset.code}</span>
                          </Button>
                          {canDelete && (
                            <Button hint={"Delete this asset — only possible if it has no history"}
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-muted-foreground hover:text-destructive"
                              onClick={() => setDeletingOne(asset)}
                            >
                              <Trash2 className="size-3.5" />
                              <span className="sr-only">Delete {asset.code}</span>
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={(canManage ? 10 : 9) + (canSelect ? 1 : 0)}>
                      {search || status || categoryId || warranty ? (
                        <EmptyState
                          art={LostArt}
                          title="Nothing matches those filters"
                          body="It may be archived, spelt differently, or filed under another status or type."
                        >
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSearchInput('');
                              setParams(new URLSearchParams(), { replace: true });
                            }}
                          >
                            Clear filters
                          </Button>
                        </EmptyState>
                      ) : (
                        <EmptyState
                          art={DeviceArt}
                          title="No assets registered yet"
                          body="Register laptops, phones and accessories once — then issue, track and recover them here."
                        >
                          {canManage && (
                            <Button onClick={() => setFormOpen(true)}>
                              <Plus className="mr-1.5 size-4" /> Register asset
                            </Button>
                          )}
                        </EmptyState>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm">
              <span className="at-num text-muted-foreground">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page <= 1}
                  onClick={() => setFilter('page', String(page - 1))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={page >= lastPage}
                  onClick={() => setFilter('page', String(page + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <DeleteRecordDialog
        open={deletingOne !== null}
        onOpenChange={(open) => !open && setDeletingOne(null)}
        label={deletingOne ? `${deletingOne.code} — ${deletingOne.name}` : ''}
        noun="asset"
        alternative="retire it instead, which takes it out of service and keeps the record of who held it."
        onDelete={() => deleteAsset.mutateAsync(deletingOne!.id)}
      />

      <BulkConfirmDialog
        open={confirmingAck}
        onOpenChange={setConfirmingAck}
        assetIds={selection.ids}
        onPage={data?.assets ?? []}
        onDone={selection.clear}
      />

      <BulkVerifyDialog
        open={verifying}
        onOpenChange={setVerifying}
        assetIds={selection.ids}
        onDone={selection.clear}
      />

      <BulkDeleteDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        count={selection.ids.length}
        noun="asset"
        onConfirm={async () => {
          const result = await bulkDelete.mutateAsync(selection.ids);
          // Only the rows that actually went are cleared, so anything kept stays
          // ticked and visible rather than silently losing its selection.
          const kept = new Set(result.skipped.map((sk) => sk.id));
          selection.toggleAll(
            selection.ids.filter((id) => !kept.has(id)),
            false,
          );
          return result;
        }}
      />

      {issuing && (
        <AssignDialog asset={issuing} onOpenChange={(open) => !open && setIssuing(null)} />
      )}
      {returning && (
        <ReturnDialog asset={returning} onOpenChange={(open) => !open && setReturning(null)} />
      )}
      {repairing && (
        <OpenRepairDialog
          asset={repairing}
          open
          onOpenChange={(open) => !open && setRepairing(null)}
        />
      )}

      <AssetFormDialog open={formOpen} onOpenChange={setFormOpen} />
      <AssetFormDialog
        open={editingId !== null}
        onOpenChange={(open) => !open && setEditingId(null)}
        assetId={editingId ?? undefined}
      />
    </PageContainer>
  );
}

/**
 * "I have just walked this room and seen all of these."
 *
 * The location is asked for once and written to every asset in the batch, because that
 * is the whole reason to verify a room at once - everything in it is in the same room.
 * Left blank it changes nothing, so a mixed selection from three floors can still be
 * stamped without lying about where any of it is.
 *
 * Unlike the acknowledgement dialog beside it there is no note and nothing is refused
 * as already-done. A verification is re-stamped by design: the useful answer is the
 * most recent one, and an asset checked twice in a week is not a problem to prevent.
 */
function BulkVerifyDialog({
  open,
  onOpenChange,
  assetIds,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetIds: string[];
  onDone: () => void;
}) {
  const verify = useVerifyAssets();
  const { data: suggestions } = useLocationSuggestions();
  const [location, setLocation] = React.useState('');

  React.useEffect(() => {
    if (open) setLocation('');
  }, [open]);

  const submit = async () => {
    try {
      const result = await verify.mutateAsync({
        ids: assetIds,
        location: location.trim() || undefined,
      });
      toast.success(
        result.skipped > 0
          ? `${result.verified} confirmed as seen. ${result.skipped} left alone — retired, disposed of, or already gone.`
          : `${result.verified} confirmed as seen today.`,
      );
      onDone();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Confirm {assetIds.length} asset{assetIds.length === 1 ? '' : 's'} as seen
          </DialogTitle>
          <DialogDescription>
            Records today as the last time anyone physically checked these. This is what the
            register could never answer on its own — it knows who <em>should</em> have a laptop,
            not whether it still exists.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>Where are they? (optional)</Label>
          <SuggestionPicker
            noun="location"
            suggestions={suggestions?.locations}
            emptyText="No locations recorded yet — type the first"
            value={location}
            onChange={setLocation}
          />
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Written to every asset in this selection, so use it when you have walked one room.
            Leave it blank and each asset keeps the location it already has — nothing is cleared.
          </p>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button hint={"Record that you saw these assets today"} onClick={() => void submit()} disabled={verify.isPending}>
            {verify.isPending ? 'Recording…' : 'Confirm as seen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirming a selection of handovers on the holders' behalf.
 *
 * One note covers the whole batch, because that is how the confirmation was actually
 * collected - a signed sheet, a floor walk with a list. It is required for the same
 * reason as the single one: an administrator ticking a box is not evidence of anything
 * on its own, and the line saying how they know is all a later reader has.
 *
 * Anything in the selection that has no open handover, or that the holder has already
 * confirmed themselves, is left alone by the API and reported back as skipped - a first
 * acknowledgement is never overwritten.
 */
function BulkConfirmDialog({
  open,
  onOpenChange,
  assetIds,
  onPage,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetIds: string[];
  /** The rows currently loaded, so the dialog can say what is actually confirmable. */
  onPage: AssetSummary[];
  onDone: () => void;
}) {
  const confirmMany = useAcknowledgeOnBehalf();
  const [note, setNote] = React.useState('');

  // The API is the authority - it re-stamps nothing and reports what it skipped - but
  // the dialog must not OFFER to confirm a handover its holder already confirmed. What
  // is on screen can be checked here; a selection carried over from another page cannot,
  // and is counted as unknown rather than guessed at.
  const known = new Map(onPage.map((asset) => [asset.id, asset]));
  const seen = assetIds.map((id) => known.get(id)).filter((asset) => asset !== undefined);
  const confirmable = seen.filter((asset) => asset.holder && !asset.acknowledged);
  const alreadyDone = seen.filter((asset) => asset.holder && asset.acknowledged);
  const notOut = seen.filter((asset) => !asset.holder);
  const unknown = assetIds.length - seen.length;
  const nothingToDo = confirmable.length === 0 && unknown === 0;

  React.useEffect(() => {
    if (open) setNote('');
  }, [open]);

  const submit = async () => {
    try {
      const result = await confirmMany.mutateAsync({ assetIds, note: note.trim() });
      toast.success(
        result.skipped > 0
          ? `${result.confirmed} confirmed. ${result.skipped} left alone - already confirmed, or not out with anyone.`
          : `${result.confirmed} confirmed on their holders' behalf.`,
      );
      onDone();
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {nothingToDo
              ? 'Nothing here to confirm'
              : `Confirm ${confirmable.length + unknown} handover${
                  confirmable.length + unknown === 1 ? '' : 's'
                } on their behalf`}
          </DialogTitle>
          <DialogDescription>
            {nothingToDo ? (
              <>
                Everything selected has been dealt with already
                {alreadyDone.length > 0 &&
                  ` - ${alreadyDone.length} confirmed by the people holding them`}
                {notOut.length > 0 &&
                  `${alreadyDone.length > 0 ? ', and' : ' -'} ${notOut.length} not out with anyone`}
                . A confirmation is never re-stamped: the first one is the one that means something,
                and overwriting it would move the date a dispute turns on.
              </>
            ) : (
              <>
                Recorded as <strong>you</strong> confirming these for the people holding them, not
                as them confirming. Their own confirmation is stronger evidence, so use this for
                handovers people have already confirmed to you some other way.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!nothingToDo && (alreadyDone.length > 0 || notOut.length > 0) && (
          <p className="rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
            {alreadyDone.length > 0 && (
              <>
                {alreadyDone.length} of these {alreadyDone.length === 1 ? 'is' : 'are'} already
                confirmed by the person holding {alreadyDone.length === 1 ? 'it' : 'them'}, and will
                be left exactly as {alreadyDone.length === 1 ? 'it is' : 'they are'}.{' '}
              </>
            )}
            {notOut.length > 0 && (
              <>
                {notOut.length} {notOut.length === 1 ? 'is' : 'are'} not out with anyone, so there
                is nothing to confirm.
              </>
            )}
          </p>
        )}

        <div className="space-y-1.5" hidden={nothingToDo}>
          <Label>How did they confirm these?</Label>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Signed handover sheets collected during the 26 Aug floor check."
          />
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            Required, and written against every one of them. Anything already confirmed by its
            holder is left exactly as it is.
          </p>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {nothingToDo ? 'Close' : 'Cancel'}
          </Button>
          {!nothingToDo && (
            <>
            <Button hint={"Record receipt for these assets on their holders' behalf"} onClick={() => void submit()} disabled={confirmMany.isPending || !note.trim()}>
              {confirmMany.isPending ? 'Recording…' : 'Record confirmations'}
            </Button>
            {!note.trim() && <span className="self-center text-xs text-muted-foreground">Add a note first</span>}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
